import React, {
  useState,
  useEffect,
  useContext,
  useRef,
} from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTrash,
  faUpload,
  faFileImport,
  faFileCode,
} from "@fortawesome/free-solid-svg-icons";
import { CanvasContext } from "../../../contexts/CanvasContext";
import indexedDBService from "../../../services/IndexedDBService";
import JSONEditor from "./JSONEditor";
import "./FileManager.css";

const FileManager = ({ show_preview }) => {
  const [search_term, set_search_term] = useState("");
  const [file_type, set_file_type] = useState("All files");
  const [upload_message, set_upload_message] = useState("");
  const [show_json_editor, set_show_json_editor] = useState(false);
  const [selected_json_file, set_selected_json_file] = useState(null);
  const [context_menu, set_context_menu] = useState({
    visible: false,
    x: 0,
    y: 0,
    file: null,
  });
  const [renaming_file, set_renaming_file] = useState(null);
  const rename_input_ref = useRef(null);

  const { files, setFiles, showFileManager, LayOut } =
    useContext(CanvasContext);

  useEffect(() => {
    const loadAllFiles = async () => {
      const allSavedFiles = await indexedDBService.getFiles();
      setFiles(allSavedFiles || []);
    };
    loadAllFiles();
  }, [setFiles]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (context_menu.visible && !event.target.closest(".context-menu_file")) {
        set_context_menu({ visible: false, x: 0, y: 0, file: null });
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [context_menu]);

  const handle_file_upload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const existing_file = files.find(
        (f) =>
          f.name === file.name && f.size === file.size && f.type === file.type
      );

      if (existing_file) {
        set_upload_message("This file has already been uploaded.");
        setTimeout(() => set_upload_message(""), 3000);
        return;
      }

      const new_file = {
        id: Date.now(),
        name: file.name,
        type: file.type,
        size: file.size,
        uploadDate: new Date().toLocaleString(),
        data: await file_to_data_url(file),
      };
      await indexedDBService.saveFile(new_file);
      setFiles((prevFiles) => [...prevFiles, new_file]);
      set_upload_message("File uploaded successfully.");
      setTimeout(() => set_upload_message(""), 3000);
    }
  };

  const file_to_data_url = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const delete_file = async (file_id) => {
    await indexedDBService.deleteFile(file_id);
    setFiles(files.filter((file) => file.id !== file_id));
  };

  const handle_json_import = async (event) => {
    const file = event.target.files[0];
    if (file && file.type === "application/json") {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const json_content = JSON.parse(e.target.result);
          const new_file = {
            id: Date.now(),
            name: file.name,
            type: file.type,
            size: file.size,
            uploadDate: new Date().toLocaleString(),
            data: JSON.stringify(json_content),
          };
          await indexedDBService.saveFile(new_file);
          setFiles([...files, new_file]);
          set_upload_message("JSON file imported successfully.");
        } catch (error) {
          set_upload_message("Error importing JSON file: " + error.message);
        }
        setTimeout(() => set_upload_message(""), 3000);
      };
      reader.readAsText(file);
    } else {
      set_upload_message("Please select a valid JSON file.");
      setTimeout(() => set_upload_message(""), 3000);
    }
  };

  const handle_create_json = () => {
    set_show_json_editor(true);
  };

  const handle_save_json = async (json_content) => {
    try {
      if (selected_json_file) {
        // Update existing file
        const updated_file = {
          ...selected_json_file,
          data: json_content,
          size: json_content.length,
          uploadDate: new Date().toLocaleString(),
        };
        await indexedDBService.updateFile(updated_file);
        setFiles(
          files.map((f) => (f.id === updated_file.id ? updated_file : f))
        );
        set_upload_message("JSON file updated successfully.");
      } else {
        // Create new file
        const new_file = {
          id: Date.now(),
          name: `new_json_${Date.now()}.json`,
          type: "application/json",
          size: json_content.length,
          uploadDate: new Date().toLocaleString(),
          data: json_content,
        };
        await indexedDBService.saveFile(new_file);
        setFiles([...files, new_file]);
        set_upload_message("New JSON file created and saved successfully.");
      }
      set_show_json_editor(false);
      set_selected_json_file(null);
    } catch (error) {
      set_upload_message("Error saving JSON file: " + error.message);
    }
    setTimeout(() => set_upload_message(""), 3000);
  };

  const handle_cancel_json = () => {
    set_show_json_editor(false);
    set_selected_json_file(null);
  };

  const handle_context_menu = (event, file) => {
    event.preventDefault();
    set_context_menu({
      visible: true,
      x: event.clientX,
      y: event.clientY,
      file: file,
    });
  };

  const handle_open_json = (file) => {
    if (file.type === "application/json") {
      set_selected_json_file(file);
      set_show_json_editor(true);
    }
    set_context_menu({ visible: false, x: 0, y: 0, file: null });
  };

  const handle_rename_start = (file) => {
    set_renaming_file(file);
    set_context_menu({ visible: false, x: 0, y: 0, file: null });
    setTimeout(() => {
      if (rename_input_ref.current) {
        rename_input_ref.current.focus();
        rename_input_ref.current.select();
      }
    }, 0);
  };

  const handle_rename_end = async (event) => {
    if (event.key === "Enter" || event.type === "blur") {
      const new_name = event.target.value;
      if (new_name && new_name !== renaming_file.name) {
        const updated_file = { ...renaming_file, name: new_name };
        try {
          await indexedDBService.updateFile(updated_file);
          setFiles(
            files.map((f) => (f.id === updated_file.id ? updated_file : f))
          );
          set_upload_message("File renamed successfully.");
        } catch (error) {
          console.error("Error updating file:", error);
          set_upload_message("Error renaming file: " + error.message);
        }
        setTimeout(() => set_upload_message(""), 3000);
      }
      set_renaming_file(null);
    } else if (event.key === "Escape") {
      set_renaming_file(null);
    }
  };

  const filtered_files = files.filter((file) => {
    const matches_search = file.name
      .toLowerCase()
      .includes(search_term.toLowerCase());
    const matches_type =
      file_type === "All files" ||
      file.type.startsWith(file_type.toLowerCase());
    return matches_search && matches_type;
  });

  const classname_for_layout =
    LayOut === 0
      ? show_preview
        ? "file_miniFile_manager_preview"
        : "file_miniFile_manager"
      : "file_miniFile_manager_OneTwo";

  return (
    <>
      <div className={classname_for_layout}>
        <div
          className="file-manager"
          style={{ display: showFileManager ? "block" : "none" }}
        >
          <h2>File Manager</h2>
          <div className="file-manager-controls">
            <input
              type="text"
              placeholder="Search files..."
              value={search_term}
              onChange={(e) => set_search_term(e.target.value)}
            />
            <select
              value={file_type}
              onChange={(e) => set_file_type(e.target.value)}
            >
              <option>All files</option>
              <option>image</option>
              <option>video</option>
              <option>audio</option>
              <option>application/json</option>
            </select>
            <button
              onClick={() => document.getElementById("file-upload").click()}
            >
              <FontAwesomeIcon icon={faUpload} /> Upload
            </button>
            <input
              id="file-upload"
              type="file"
              style={{ display: "none" }}
              onChange={handle_file_upload}
            />
            <button
              onClick={() => document.getElementById("json-import").click()}
            >
              <FontAwesomeIcon icon={faFileImport} /> Import JSON
            </button>
            <input
              id="json-import"
              type="file"
              accept="application/json"
              style={{ display: "none" }}
              onChange={handle_json_import}
            />
            <button onClick={handle_create_json}>
              <FontAwesomeIcon icon={faFileCode} /> Create JSON
            </button>
          </div>
          {upload_message && <p className="upload-message">{upload_message}</p>}

          <table className="file-list">
            <thead>
              <tr>
                <th>Preview</th>
                <th>File name</th>
                <th>Size</th>
                <th>Type</th>
                <th>Upload date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered_files.map((file) => (
                <tr
                  key={file.id}
                  onContextMenu={(e) => handle_context_menu(e, file)}
                >
                  <td>
                    {file.type.startsWith("image") && (
                      <img
                        src={file.data}
                        alt={file.name}
                        style={{ width: "50px", height: "50px" }}
                      />
                    )}
                  </td>
                  <td>
                    {renaming_file && renaming_file.id === file.id ? (
                      <input
                        ref={rename_input_ref}
                        type="text"
                        defaultValue={file.name}
                        onKeyDown={handle_rename_end}
                        onBlur={handle_rename_end}
                      />
                    ) : (
                      file.name
                    )}
                  </td>
                  <td>{(file.size / 1024 / 1024).toFixed(2)} MB</td>
                  <td>{file.type}</td>
                  <td>{file.uploadDate}</td>
                  <td>
                    <button onClick={() => delete_file(file.id)}>
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {context_menu.visible && (
        <div
          className="context-menu_file"
          style={{
            position: "fixed",
            top: context_menu.y,
            left: context_menu.x,
          }}
        >
          {context_menu.file.type === "application/json" && (
            <button onClick={() => handle_open_json(context_menu.file)}>
              Open
            </button>
          )}
          <hr />
          <button onClick={() => handle_rename_start(context_menu.file)}>
            Rename
          </button>
        </div>
      )}

      {show_json_editor && (
        <div className="json-editor-overlay">
          <JSONEditor
            initialContent={
              selected_json_file ? selected_json_file.data : undefined
            }
            onSave={handle_save_json}
            onCancel={handle_cancel_json}
          />
        </div>
      )}
    </>
  );
};

export default FileManager;
